import { NotificationEnum } from "@graphql/generated/graphql.g";
import { ComponentProps, forwardRef, useImperativeHandle } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { Button } from "@design-system/Button/Button";
import { FieldGroup } from "@design-system/FieldGroup/FieldGroup";
import { Form } from "@design-system/Form/Form";
import { Select } from "@design-system/Select/Select";
import { TextField } from "@design-system/TextField/TextField";

export type ClientNotificationFilterFormValues = {
  notificationType?: NotificationEnum[] | undefined;
  applicationNameSearch?: string | undefined;
};

export type ClientNotificationFilterFormRef = {
  getCurrentValues: () => ClientNotificationFilterFormValues;
};

type Props = {
  submitText?: string;
  onFilterChange: SubmitHandler<ClientNotificationFilterFormValues>;
  values: ClientNotificationFilterFormValues;
  errorsAPI?: ComponentProps<typeof Form>["errorsAPI"];
  disabled?: boolean;
  withSubmitButton?: boolean;
};

export const ClientNotificationFilterForm = forwardRef<
  ClientNotificationFilterFormRef,
  Props
>(({ onFilterChange, values, errorsAPI }, ref) => {
  const { handleSubmit, setValue, watch, getValues } =
    useForm<ClientNotificationFilterFormValues>({
      values: {
        notificationType: values.notificationType ?? [],
        applicationNameSearch: values.applicationNameSearch ?? "",
      },
    });

  const NOTIFICATION_TYPE_OPTIONS: Array<{
    id: NotificationEnum;
    label: string;
  }> = [
    {
      id: NotificationEnum.SharedLogin,
      label: "Shared Login",
    },
    {
      id: NotificationEnum.SaasDiscovered,
      label: "SaaS Discovered",
    },
  ];

  const notificationType = watch("notificationType");
  const applicationNameSearch = watch("applicationNameSearch");

  // Expose getCurrentValues method to parent via ref
  useImperativeHandle(
    ref,
    () => ({
      getCurrentValues: () => getValues(),
    }),
    [getValues],
  );

  const createHandleChange = (
    fieldName: keyof ClientNotificationFilterFormValues,
  ) => {
    return (value: string | string[]) => {
      if (fieldName === "notificationType") {
        setValue(fieldName, value as NotificationEnum[], { shouldDirty: true });
      } else if (fieldName === "applicationNameSearch") {
        const stringValue = Array.isArray(value) ? value[0] : value;
        setValue(fieldName, stringValue || "", { shouldDirty: true });
      } else {
        // For other fields, handle as single values
        const enumValue = Array.isArray(value) ? value[0] : value;
        const finalValue = enumValue === "" ? undefined : enumValue;
        setValue(fieldName, finalValue as never, { shouldDirty: true });
      }
    };
  };

  return (
    <Form onSubmit={handleSubmit(onFilterChange)} errorsAPI={errorsAPI}>
      <FieldGroup label="Application name">
        <TextField
          placeholder="Search by application name"
          value={applicationNameSearch ?? ""}
          onChange={(e) =>
            setValue("applicationNameSearch", e.target.value, {
              shouldDirty: true,
            })
          }
        />
      </FieldGroup>
      <FieldGroup label="Notification type">
        <Select
          options={NOTIFICATION_TYPE_OPTIONS}
          value={notificationType ?? []}
          onChange={createHandleChange("notificationType")}
        />
      </FieldGroup>
      {/* Hidden submit button to enable Enter key submission */}
      <Button type="submit" style={{ display: "none" }} />
    </Form>
  );
});
